import {
  PageHeader,
  message,
  Modal,
  Form,
  Input,
  Select,
  Button as AntButton,
} from 'antd'
import TableBlock from '../../components/tables/TableBlock'
import { getLeads, createLead } from '../../network/MiniCRMs'
import { Space, Button } from 'antd'
import { useState } from 'react'
import { useQueryClient } from 'react-query'
import { ROLE_PERMISSIONS, SCREEN_SIZE, ROLES } from '../../constants/common'
import { useAuth } from '../../hooks/useAuth'
import { EyeOutlined, EditOutlined } from '@ant-design/icons'
import {
  createUrlWithSeparatedFilters,
  createUrlWithFiltersFromCurrentURL,
} from '../../utils/filterUtils'
import useSearchParams from '../../hooks/useQuery'
import useIsMobile from '../../hooks/useIsMobile'

const MiniCrm = ({ history }) => {
  const queryClient = useQueryClient()
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [form] = Form.useForm()
  const params = useSearchParams()
  const { user } = useAuth()
  const permissions = ROLE_PERMISSIONS[user.role]
    ? ROLE_PERMISSIONS[user.role]
    : []
  const isMobile = useIsMobile(SCREEN_SIZE.MOBILE)

  const isAdmin = user.role === ROLES.ADMIN || user.role === ROLES.SUPER_ADMIN

  const handleCreateNewLead = async (values) => {
    try {
      const leadData = {
        firstName: values.firstName,
        lastName: values.lastName,
        type: values.type,
        note: values.note,
        owner: user.id,
      }
      await createLead(leadData)
      message.success('Lead created successfully')
      queryClient.invalidateQueries(['getLeads'])
      setIsCreateModalVisible(false)
      form.resetFields()
    } catch (error) {
      message.error('Failed to create event')
      console.error('Error creating event:', error)
    }
  }

  const showCreateModal = () => {
    setIsCreateModalVisible(true)
  }

  const handleCancelCreate = () => {
    setIsCreateModalVisible(false)
    form.resetFields()
  }

  const getRowClassName = (record) => {
    const type = record.type?.toLowerCase()
    switch (type) {
      case 'potential lead':
        return 'potential-lead'
      case 'qualified lead':
        return 'qualified-lead'
      default:
        return ''
    }
  }

  let filters = (history) => ({
    fields: [
      ...(isAdmin
        ? [
            {
              type: 'select',
              label: 'Leads',
              key: 'leadFilter',
              noAll: true,
              options: [
                { label: 'My Leads', key: 'my' },
                { label: 'All Leads', key: 'all' },
              ],
            },
          ]
        : []),
      {
        type: 'text',
        label: 'Name',
        key: 'fullname',
        noRegex: true,
      },
      {
        type: 'select',
        label: 'Type',
        key: 'type',
        options: [
          { label: 'Potential Lead', key: 'potential lead' },
          { label: 'Qualified Lead', key: 'qualified lead' },
        ],
      },
    ],
    actions: [
      {
        label: '+ Create a lead',
        isActive: permissions.includes('can-create-leads'),
        action: showCreateModal,
      },
    ],
  })

  const navigateToLeadDetail = (leadId, keyRequest) => {
    let currentPage = null
    const filterValues = params.get('filterValues')
    if (filterValues) {
      try {
        const allFilters = JSON.parse(decodeURIComponent(filterValues))
        if (allFilters._page) {
          currentPage = parseInt(allFilters._page, 10)
        }
      } catch (e) {}
    }
    if (!currentPage) {
      const pageParam = params.get('page')
      if (pageParam) {
        currentPage = parseInt(pageParam, 10)
      }
    }

    const url = createUrlWithSeparatedFilters(
      `/mini-crm/${leadId}`,
      keyRequest,
      currentPage
    )
    history.push(url)
  }

  const columns = (history, keyRequest) => {
    const baseColumns = [
      {
        title: 'Name',
        key: 'fulllame',
        dataIndex: 'fullname',
      },
      {
        title: 'Type',
        key: 'type',
        dataIndex: 'type',
      },
    ]

    if (!isMobile) {
      baseColumns.push({
        title: 'Notes',
        key: 'note',
        dataIndex: 'note',
      })
    }

    if (!isMobile) {
      baseColumns.push({
        title: '',
        key: 'action',
        fixed: 'right',
        width: permissions.includes('can-edit-leads') ? 100 : 50,
        render: (e) => {
          return (
            <Space>
              <Button
                type="primary"
                shape="circle"
                icon={<EyeOutlined />}
                onClick={(event) => {
                  event.stopPropagation()
                  navigateToLeadDetail(e.id, keyRequest)
                }}
              />
              {permissions.includes('can-edit-leads') && (
                <Button
                  style={styles.iconEdit}
                  type="primary"
                  shape="circle"
                  icon={<EditOutlined />}
                  onClick={(event) => {
                    event.stopPropagation()
                    let currentPage = null
                    const filterValues = params.get('filterValues')
                    if (filterValues) {
                      try {
                        const allFilters = JSON.parse(
                          decodeURIComponent(filterValues)
                        )
                        if (allFilters._page) {
                          currentPage = parseInt(allFilters._page, 10)
                        }
                      } catch (e) {}
                    }
                    if (!currentPage) {
                      const pageParam = params.get('page')
                      if (pageParam) {
                        currentPage = parseInt(pageParam, 10)
                      }
                    }

                    const url = createUrlWithSeparatedFilters(
                      `/mini-crm/${e.id}/edit`,
                      keyRequest,
                      currentPage
                    )
                    // Parse URL to separate pathname and search
                    const [pathname, search] = url.split('?')
                    history.push({
                      pathname,
                      search: search ? `?${search}` : '',
                      state: { fromDetail: false },
                    })
                  }}
                />
              )}
            </Space>
          )
        },
      })
    }

    return baseColumns
  }

  const handleRowClick = (record) => {
    if (isMobile) {
      const url = createUrlWithFiltersFromCurrentURL(`/mini-crm/${record.id}`)
      history.push(url)
    }
  }

  return (
    <>
      <PageHeader className="site-page-header" title="Contact lead" />
      <div className="crm-wrapper">
        <TableBlock
          columns={columns}
          filters={filters}
          name={'getLeads'}
          request={getLeads}
          pageSize={100}
          otherFilters={isAdmin ? { leadFilter: 'my' } : {}} // Set the default filter to my leads for admin
          onRow={(record) => ({
            className: getRowClassName(record),
            ...(isMobile && {
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' },
            }),
          })}
        />
      </div>

      <Modal
        title="Create New Lead"
        open={isCreateModalVisible}
        onCancel={handleCancelCreate}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateNewLead}>
          <Form.Item label="First Name" name="firstName">
            <Input placeholder="Enter first name" />
          </Form.Item>

          <Form.Item label="Last Name" name="lastName">
            <Input placeholder="Enter last name" />
          </Form.Item>

          <Form.Item label="Type" name="type">
            <Select placeholder="Select a type">
              <Select.Option value="potential lead">
                Potential Lead
              </Select.Option>
              <Select.Option value="qualified lead">
                Qualified Lead
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Note" name="note">
            <Input.TextArea rows={4} placeholder="Note for this lead" />
          </Form.Item>

          <Form.Item>
            <Space>
              <AntButton type="primary" htmlType="submit">
                Create Lead
              </AntButton>
              <AntButton onClick={handleCancelCreate}>Cancel</AntButton>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default MiniCrm

const styles = {
  iconEdit: {
    color: '#fff',
  },
}
